import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeSetters } from '@frontend/scope';
import { useWorkspaceShallowStore } from '@frontend/workspaces';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  CaretLeftIconSmall,
  Modal,
  ModalControlResponse,
  Text,
  TextLink,
} from '@frontend/design-system';
import { usePhoneSyncMutations } from '../use-phone-sync-mutations';

/**
 * Self-contained modal for workspace mode losing-device-access scenario.
 *
 * All workspace store and scope store subscriptions live here so
 * the parent (location-picker hook) is never re-rendered by them.
 *
 * Props are intentionally minimal — only modal control and a
 * continuation callback that the hook uses to finalize the location change.
 */
export const WorkspaceScopedLosingAccessModal = ({
  modal,
  onContinue,
}: {
  modal: ModalControlResponse;
  onContinue: () => void;
}) => {
  const { t } = useTranslation('phoneSync');

  // Workspace cleanup — subscribed here, isolated from parent
  const { pendingWorkspaceId, setPendingWorkspaceId, setSelectedWorkspaceId, commitPendingWorkspace } =
    useWorkspaceShallowStore(
      'pendingWorkspaceId',
      'setPendingWorkspaceId',
      'setSelectedWorkspaceId',
      'commitPendingWorkspace'
    );
  const { setSelectedScope } = useAppScopeSetters();

  // Phone cleanup
  const { disconnectDevice } = usePhoneSyncMutations();
  const { setPhoneConfig } = usePhoneConfigShallowStore('setPhoneConfig');

  const handleGoBack = () => {
    modal.closeModal();
    // Only clear workspace state when we actually have a pending workspace
    if (pendingWorkspaceId) {
      setPendingWorkspaceId(null);
      setSelectedWorkspaceId(null);
      setSelectedScope(undefined);
    }
  };

  const handleContinue = () => {
    modal.closeModal();
    disconnectDevice();
    setPhoneConfig(undefined);
    commitPendingWorkspace();
    onContinue();
  };

  return (
    <Modal {...modal.modalProps} maxWidth={600}>
      <Modal.Header textAlign='left'>{t('Losing Device Access')}</Modal.Header>
      <Modal.Body>
        <Text css={{ marginBlock: '0.25em' }}>
          {t(
            'In workspace mode, you will no longer have access to a device. As a result, you will not be able to place or receive calls within the Weave app.'
          )}
        </Text>
        <Text css={{ marginBlock: '0.25em' }}>
          {t('To resume device access, exit workspace and update location selections')}
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <ButtonBar
          removeSpacing
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            justify-content: space-between;
          `}
        >
          <TextLink
            weight='bold'
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              width: 'auto',
            }}
            onClick={handleGoBack}
            trackingId='phn-portal-losingAccess-workspace-btn-goBack'
            data-test-id='loseAccess-workspace-goBack-btn'
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <CaretLeftIconSmall /> {t('Go Back')}
            </span>
          </TextLink>

          <Button
            css={{ width: 'auto' }}
            size='large'
            onClick={handleContinue}
            trackingId='phn-portal-losingAccess-workspace-btn-continue'
          >
            {t('Continue')}
          </Button>
        </ButtonBar>
      </Modal.Footer>
    </Modal>
  );
};
